'use client';

import { useTranslations } from 'next-intl';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/components/ui/button/Button.types';
import { Colors } from '@/lib/colorUtils';
import { resolveLogoSrc } from '@/lib/theme-utils';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { AddToAppleWallet } from '@/components/ui/add-to-apple-wallet';
import { AddToGoogleWallet } from '@/components/ui/add-to-google-wallet';

export interface LoyaltyQrModalProps {
  logo?: string;
  companyName?: string;
  userName: string;
  lcnDisplay: string;
  largeQrSrc: string;
  onClose: () => void;
  /** When provided, renders Add to Apple/Google Wallet buttons below the QR card */
  applePassUrl?: string;
  googlePassJwt?: string;
}

export function LoyaltyQrModal({
  logo,
  companyName,
  userName,
  lcnDisplay,
  largeQrSrc,
  onClose,
  applePassUrl,
  googlePassJwt,
}: LoyaltyQrModalProps) {
  const tCommon = useTranslations('Common');
  const tLoyalty = useTranslations('Loyalty');

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={tLoyalty('showQr')}
      ariaLabel={tLoyalty('loyaltyQrCode')}
      // dvh (visible viewport) so the tall QR sheet can't extend behind the iOS Safari address bar
      className="tw:max-h-[85dvh]"
      bodyClassName="tw:flex tw:flex-col tw:p-huge tw:bg-gray-lighter tw:gap-huge"
    >
      <div className="tw:bg-white tw:rounded-16 tw:pt-base tw:px-huge tw:pb-gargantuan tw:flex tw:flex-col tw:items-center" data-cy="loyalty-qr-modal">
        <div className="tw:flex tw:items-start tw:justify-between tw:w-full tw:pb-gargantuan">
          {logo && (
            /* TODO: contrast check required for logo on primary bg */
            <div className="tw:rounded-4 tw:px-small tw:py-nano tw:mb-nano tw:w-fit tw:bg-primary">
              <img
                src={resolveLogoSrc(logo)}
                alt={tLoyalty('companyLogoAlt', { companyName: companyName || 'Company' })}
                className="tw:max-h-[3rem] tw:max-w-[6rem] tw:object-contain"
                data-cy="loyalty-qr-modal-logo"
              />
            </div>
          )}
          <Text size="16" weight="bold" color={Colors.colorGrayDarker}>
            {tLoyalty('loyaltyCard')}
          </Text>
        </div>
        <Text size="24" weight="bold" color={Colors.colorFont} className="tw:text-center tw:mb-base" data-cy="loyalty-qr-modal-name">
          {userName}
        </Text>

        <div className="tw:w-full tw:flex tw:items-center tw:gap-small tw:min-w-0 tw:mb-gargantuan">
          <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
          <div className="tw:bg-gray-lighter tw:rounded-full tw:px-base tw:py-micro tw:shrink-0">
            <Text size="14" weight="bold" color={Colors.colorFont} data-cy="loyalty-qr-modal-number">
              {lcnDisplay}
            </Text>
          </div>
          <div className="tw:flex-1 tw:h-px tw:bg-gray-light" />
        </div>
        {largeQrSrc && (
          <img src={largeQrSrc} alt={tLoyalty('qrCodeAlt')} className="tw:w-[12rem] tw:h-[12rem]" data-cy="loyalty-qr-modal-qr" />
        )}
        <Text
          size="14"
          weight="regular"
          color={Colors.colorGrayDark}
          className="tw:text-center tw:mt-base"
          data-cy="loyalty-qr-modal-instruction"
        >
          {tLoyalty('scanAtGarage')}
        </Text>
      </div>

      {(applePassUrl || googlePassJwt) && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-base">
          {applePassUrl && <AddToAppleWallet url={applePassUrl} />}
          {googlePassJwt && <AddToGoogleWallet jwt={googlePassJwt} />}
        </div>
      )}

      <Button
        buttonType={ButtonType.button}
        size={ButtonSize.large}
        variant={ButtonVariant.primary}
        onClick={onClose}
        className="tw:w-full"
        data-cy="loyalty-qr-modal-done"
      >
        {tCommon('done')}
      </Button>
    </BottomSheet>
  );
}
